import { motion } from "framer-motion";
import { Zap, Settings2, Ruler } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HOLIDAY_IMAGES } from "../../data/holidays";
import { Chapter, Reveal, EASE } from "./shared";

export default function HolidayBike() {
  const { t } = useTranslation();
  const B = HOLIDAY_IMAGES.bike;
  return (
    <section className="bg-forest text-bone py-24 lg:py-40 relative overflow-hidden" data-testid="holidays-bike">
      <div className="absolute inset-0 grain pointer-events-none" />
      {/* Spotlight */}
      <div className="absolute -top-40 -right-40 w-[42rem] h-[42rem] rounded-full bg-bone/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <motion.div
            initial={{ clipPath: "inset(0 100% 0 0)" }}
            whileInView={{ clipPath: "inset(0 0% 0 0)" }}
            viewport={{ once: true, margin: "0px 0px -100px 0px" }}
            transition={{ duration: 1.4, ease: EASE }}
            className="relative aspect-[16/10] overflow-hidden bg-forest-deep group"
          >
            <img src={B.rocks} alt={t("holidays.bike.alts.rocks")} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/60 via-transparent to-transparent" />
          </motion.div>
          <div className="grid grid-cols-2 gap-3 md:gap-5 mt-3 md:mt-5">
            <Reveal delay={0.15} className="relative aspect-[4/3] overflow-hidden bg-forest-deep group">
              <img src={B.track} alt={t("holidays.bike.alts.track")} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
            </Reveal>
            <Reveal delay={0.25} className="relative aspect-[4/3] overflow-hidden bg-forest-deep group">
              <img src={B.sunset} alt={t("holidays.bike.alts.sunset")} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2">
          <Chapter number={t("holidays.bike.chapter")} label={t("holidays.bike.eyebrow")} tone="dark" />
          <Reveal delay={0.1}>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] mt-8 uppercase tracking-tight">
              Specialized<br /><span className="italic normal-case">Turbo Levo</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="mt-8 space-y-5 text-base md:text-lg text-bone/80 leading-relaxed">
            <p className="text-bone">{t("holidays.bike.p1")}</p>
            <p>{t("holidays.bike.p2")}</p>
            <p>{t("holidays.bike.p3")}</p>
          </Reveal>
          <Reveal delay={0.3} className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-bone/10 border border-bone/10">
            <Spec icon={<Zap className="w-4 h-4" />} label={t("holidays.bike.specs.type")} />
            <Spec icon={<Settings2 className="w-4 h-4" />} label={t("holidays.bike.specs.prep")} />
            <Spec icon={<Ruler className="w-4 h-4" />} label={t("holidays.bike.specs.sizing")} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Spec({ icon, label }) {
  return (
    <div className="bg-forest p-5 flex flex-col gap-3">
      <span className="text-terracotta">{icon}</span>
      <span className="text-xs uppercase tracking-wider-2 text-bone/85 leading-snug">{label}</span>
    </div>
  );
}
